// Reading what people drop, paste or pick into the composer.
// Text files are read here; images get a small thumbnail for the conversation and are sent at full size only with the question.
// PDFs, Word, Excel and PowerPoint are sent to VERA, where Document Intelligence reads them (live mode).

import type { Attachment } from "../api/vera";

export const MAX_FILE_BYTES = 20 * 1024 * 1024;
/** Images and documents per message: the server takes 8 and VERA looks at every one of them. */
export const MAX_FILES = 8;
const TEXT_EXT = /\.(txt|md|markdown|csv|tsv|json|jsonl|log|xml|ya?ml|html?|ini|py|ipynb|js|mjs|cjs|ts|tsx|jsx|cs|sql|ps1|sh|toml|css)$/i;
const DOC_EXT = /\.(pdf|docx?|xlsx?|pptx?|rtf|odt)$/i;
const BLOCKED = /(^|[\\/])\.env|\.(pem|key|pfx|p12|kdbx)$/i;

export function fmtBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

async function thumbnail(dataUrl: string, max = 320): Promise<{ thumb: string; width: number; height: number }> {
  const img = new Image();
  img.src = dataUrl;
  await img.decode();
  const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
  const c = document.createElement("canvas");
  c.width = Math.max(1, Math.round(img.naturalWidth * scale));
  c.height = Math.max(1, Math.round(img.naturalHeight * scale));
  c.getContext("2d")!.drawImage(img, 0, 0, c.width, c.height);
  return { thumb: c.toDataURL("image/jpeg", 0.8), width: img.naturalWidth, height: img.naturalHeight };
}

export type ReadResult = { files: Attachment[]; skipped: string[] };

/**
 * Files to attach from a paste, or none when the paste is text. Copying from Word, Outlook or a web page puts the
 * words AND a picture of them on the clipboard: that is text to paste, not an image. A screenshot or a copied
 * image or file has no text, so it is attached.
 */
export function pastedFiles(data: DataTransfer): File[] {
  if (data.getData("text/plain").trim()) return [];
  const fromItems = Array.from(data.items).filter((i) => i.kind === "file").map((i) => i.getAsFile()).filter((f): f is File => Boolean(f));
  return fromItems.length ? fromItems : Array.from(data.files);
}

export async function readFiles(list: FileList | File[], already = 0): Promise<ReadResult> {
  const files: Attachment[] = [];
  const skipped: string[] = [];
  for (const f of Array.from(list)) {
    const name = f.name || (f.type.startsWith("image/") ? "Pasted image.png" : "File");
    if (already + files.length >= MAX_FILES) { skipped.push(`${name} (up to ${MAX_FILES} files at a time)`); continue; }
    if (BLOCKED.test(name)) { skipped.push(`${name} (secrets files are never sent)`); continue; }
    if (f.size > MAX_FILE_BYTES) { skipped.push(`${name} (over ${fmtBytes(MAX_FILE_BYTES)})`); continue; }
    try {
      if (f.type.startsWith("image/")) {
        const dataUrl = await readAsDataUrl(f);
        const t = await thumbnail(dataUrl);
        files.push({ name, text: "", kind: "image", mime: f.type, size: f.size, dataUrl, thumb: t.thumb, width: t.width, height: t.height });
      } else if (TEXT_EXT.test(name) || f.type.startsWith("text/")) {
        const text = await f.slice(0, 1_000_000).text();
        files.push({ name, text, kind: "text", mime: f.type || "text/plain", size: f.size });
      } else if (DOC_EXT.test(name)) {
        files.push({ name, text: "", kind: "document", mime: f.type, size: f.size, dataUrl: await readAsDataUrl(f) });
      } else {
        skipped.push(`${name} (VERA can't read this type yet)`);
      }
    } catch {
      skipped.push(`${name} (couldn't be read)`);
    }
  }
  return { files, skipped };
}
